import type { ExperienceBlockDraft, ResolvedVisual } from '@matter_extractor/shared';
import { LoaderCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { READER_TEST_IDS, READER_WAITING_LABEL, type ReaderMode } from '../contracts';

export interface ButtonBlockProps {
  readonly block: Extract<ExperienceBlockDraft, { type: 'button' }>;
  readonly visual: ResolvedVisual;
  readonly mode: ReaderMode;
  /** An obligatory block that has ALREADY APPEARED is missing (§9.2, H30). */
  readonly blocked: boolean;
  /** The sentence that says why it is grey. Rendered under ONE button per page. */
  readonly hint: string | null;
  /**
   * Its advance WAITS (noyau.md §4.6, N87; issue #174): the request is in
   * flight, or the determination of the noyau is not settled yet. It says its
   * waiting label and its small loading icon, on a button that stays a real,
   * disabled button (maquette noyau README).
   */
  readonly waiting: boolean;
  readonly onPress: () => void;
}

/**
 * The Bouton block (§9.2): the one way out of a page, and the one control that
 * can play the next temps (H30).
 *
 * What it does at the press is NOT decided here - `buttonBehaviour` in
 * `reader-play.ts` answers it, because « un bouton du dernier temps n'a que la
 * page suivante » and « il disparaît une fois le temps joué » are facts about
 * the page, not about the control. This renders, and calls back.
 *
 * The grey is its STATE and not its entry: « un bouton grisé par un obligatoire
 * non rempli s'anime quand même » (§9.3), which costs nothing here because the
 * animation lives on the frame.
 *
 * The hint under it is what the mockup puts there (« Cochez la case pour
 * continuer. »): a grey control that says nothing is the one thing a person
 * cannot get past.
 *
 * ## What it says while it waits (noyau.md §4.6, N83, N86; issue #174)
 *
 * Its own « Libellé d'attente », trimmed, or « Un instant... » when it has none
 * - the word of the protocol page's « Continuer », and of every Bouton stored
 * before #174. Plain text, never resolved for a variable (N84).
 *
 * A waiting label may be longer than the label - sixty characters take two or
 * three lines on a telephone (planche `AttenteLecteur`) - so the waiting state
 * is a block of centred text with the icon INLINE at the head of its first
 * line, and the button gains the line height and the vertical padding the
 * resting state has no use for (`reader.css`). The width follows the Bouton's
 * « Largeur », as for its label: nothing is ever cut.
 */
export function ButtonBlock({
  block,
  visual,
  mode,
  blocked,
  hint,
  waiting,
  onPress,
}: ButtonBlockProps): ReactNode {
  const ownWaiting = block.waitingLabel.trim();
  return (
    <>
      <button
        type="button"
        className="lecteur-bouton"
        data-style={visual.buttonStyle}
        data-waiting={waiting ? 'true' : undefined}
        disabled={blocked || waiting}
        onClick={mode === 'interactive' ? onPress : undefined}
      >
        {waiting ? (
          <span className="lecteur-attente">
            <LoaderCircle aria-hidden="true" className="lecteur-attente-icone" />
            {ownWaiting === '' ? READER_WAITING_LABEL : ownWaiting}
          </span>
        ) : (
          block.label
        )}
      </button>
      {blocked && hint !== null ? (
        <p className="lecteur-aide" data-testid={READER_TEST_IDS.advanceHint}>
          {hint}
        </p>
      ) : null}
    </>
  );
}
